import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { EdgeMailAppearancePreferences } from '@/types/appearance';
import { applyThemeModeToDocument } from '@/utils/themeMode';
import {
  DEFAULT_APP_PREFERENCES,
  type AppPreferences,
  type AppPreferencesUpdate,
} from '@/types/appPreferences';
import type { NotificationPreferences } from '@/features/edgemail-client/store/settingsStore';

export interface UserPreferencesPayload {
  appearancePreferences?: Partial<EdgeMailAppearancePreferences>;
  notificationPreferences?: Partial<NotificationPreferences>;
  appPreferences?: AppPreferencesUpdate;
}

export interface UserPreferencesState {
  appearancePreferences: EdgeMailAppearancePreferences;
  notificationPreferences: NotificationPreferences;
  appPreferences: AppPreferences;
  hasHydrated: boolean;
  setAppearancePreferences: (prefs: Partial<EdgeMailAppearancePreferences>) => void;
  setNotificationPreferences: (prefs: Partial<NotificationPreferences>) => void;
  setAppPreferences: (prefs: AppPreferencesUpdate) => void;
  setAllPreferences: (prefs: Partial<UserPreferencesPayload>) => void;
  markHydrated: () => void;
}

const DEFAULT_APPEARANCE_PREFERENCES: EdgeMailAppearancePreferences = {
  themeMode: 'dark',
  accentColor: 'blue',
  accentCustomHex: null,
  neutralBase: 'zinc',
  wallpaper: 'night-mountain',
  presetTheme: 'classic',
  wallpaperDim: 0.2,
  wallpaperBlur: 0,
  density: 'cozy',
  fontScale: 1,
  contrastMode: 'standard',
  fontCustomColor: null,
  fontBodyColor: null,
  fontDefaultColor: null,
  fontDefaultBodyColor: null,
  fontCustomColorForce: false,
  fontBodyColorForce: false,
  mailTextPrimaryColor: null,
  mailTextSecondaryColor: null,
  mailTextDefaultPrimaryColor: null,
  mailTextDefaultSecondaryColor: null,
  mailTextPrimaryColorForce: false,
  mailTextSecondaryColorForce: false,
  mailSidebarTextColor: null,
  mailSidebarTextDefaultColor: null,
  mailSidebarTextColorForce: false,
};

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  notificationsEnabled: true,
  mailNotifications: true,
  mailNewEmail: true,
  mailMentions: true,
  calendarNotifications: true,
  calendarEventReminders: true,
  documentsNotifications: true,
  documentsComments: true,
  documentsEdits: true,
  documentsMentions: true,
};

const mergeAppPreferences = (
  base: AppPreferences,
  updates?: AppPreferencesUpdate,
): AppPreferences => ({
  matters: {
    ...base.matters,
    ...(updates?.matters || {}),
  },
  documents: {
    ...base.documents,
    ...(updates?.documents || {}),
  },
  clients: {
    ...base.clients,
    ...(updates?.clients || {}),
  },
  tasks: {
    ...base.tasks,
    ...(updates?.tasks || {}),
  },
  calendar: {
    ...base.calendar,
    ...(updates?.calendar || {}),
  },
});

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set) => ({
      appearancePreferences: DEFAULT_APPEARANCE_PREFERENCES,
      notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
      appPreferences: DEFAULT_APP_PREFERENCES,
      hasHydrated: false,

      setAppearancePreferences: (prefs) => {
        if (prefs.themeMode) {
          applyThemeModeToDocument(prefs.themeMode);
        }

        set((state) => ({
          appearancePreferences: {
            ...state.appearancePreferences,
            ...prefs,
          },
        }));
      },

      setNotificationPreferences: (prefs) =>
        set((state) => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            ...prefs,
          },
        })),

      setAppPreferences: (prefs) =>
        set((state) => ({
          appPreferences: mergeAppPreferences(state.appPreferences, prefs),
        })),

      setAllPreferences: (prefs) => {
        if (prefs.appearancePreferences?.themeMode) {
          applyThemeModeToDocument(prefs.appearancePreferences.themeMode);
        }

        set((state) => ({
          appearancePreferences: {
            ...DEFAULT_APPEARANCE_PREFERENCES,
            ...state.appearancePreferences,
            ...(prefs.appearancePreferences || {}),
          },
          notificationPreferences: {
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...state.notificationPreferences,
            ...(prefs.notificationPreferences || {}),
          },
          appPreferences: mergeAppPreferences(
            mergeAppPreferences(DEFAULT_APP_PREFERENCES, state.appPreferences),
            prefs.appPreferences,
          ),
        }));
      },

      markHydrated: () => set({ hasHydrated: true }),
    }),
    {
      name: 'webelieve-user-preferences',
      partialize: (state) => ({
        appearancePreferences: state.appearancePreferences,
        notificationPreferences: state.notificationPreferences,
        appPreferences: state.appPreferences,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<UserPreferencesState> | undefined;
        return {
          ...currentState,
          appearancePreferences: {
            ...DEFAULT_APPEARANCE_PREFERENCES,
            ...(persisted?.appearancePreferences || {}),
          },
          notificationPreferences: {
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...(persisted?.notificationPreferences || {}),
          },
          appPreferences: mergeAppPreferences(
            DEFAULT_APP_PREFERENCES,
            persisted?.appPreferences,
          ),
        };
      },
    }
  )
);

export const getDefaultUserPreferences = (): UserPreferencesPayload => ({
  appearancePreferences: DEFAULT_APPEARANCE_PREFERENCES,
  notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
  appPreferences: DEFAULT_APP_PREFERENCES,
});
